/**
 * Task Board Hook
 *
 * Aggregates tasks from all sources and provides task management functions
 */

import { isFuture, isPast, isToday, isWithinInterval } from 'date-fns';
import { useMemo, useState } from 'react';
import { getGlobalDataStore } from '@/lib/mock-data/global-data-store';
import type { TaskBoardColumn, TaskFilters, TaskStatus, UnifiedTask } from '@/types/tasks';

/**
 * Map collection cases to unified tasks
 */
// biome-ignore lint/suspicious/noExplicitAny: pre-existing
function mapCollectionCasesToTasks(cases: any[]): UnifiedTask[] {
  // Only include Active cases
  const filtered = cases.filter((c) => c.status === 'Active');

  return filtered.map((c, index) => {
    // All active cases are open tasks
    const taskStatus: TaskStatus = 'open';

    return {
      id: `case-${c.caseId}`,
      type: 'collection_case' as const,
      title: `Follow up: ${c.customerName}`,
      description: `${c.nextAction || 'Follow-up required'}`,
      priority: c.priority,
      status: taskStatus,
      dueDate: c.nextActionDate,
      assignedTo: c.assignedTo || 'Unassigned',
      customerId: c.customerId,
      customerName: c.customerName,
      amount: c.displayOutstandingAmount ?? c.baseAmount ?? 0,
      sourceType: 'Collection Case',
      sourceId: c.caseId,
      sourceUrl: `/collections/case/${c.caseId}`,
      createdAt: c.createdAt,
      updatedAt: c.updatedAt,
      tags: [c.nextAction || 'Follow-up', c.status],
    };
  });
}

/**
 * Map promises to unified tasks
 */
// biome-ignore lint/suspicious/noExplicitAny: pre-existing
function mapPromisesToTasks(promises: any[], currencySymbol: string): UnifiedTask[] {
  return promises
    .filter((p) => p.status === 'Active' || p.status === 'Broken')
    .map((p, index) => {
      const isBroken = p.status === 'Broken';
      const priority = isBroken ? 'Critical' : 'High';

      // All active/broken promises are open tasks
      const taskStatus: TaskStatus = 'open';

      return {
        id: `promise-${p.id}`,
        type: 'promise' as const,
        title: isBroken ? `Broken Promise: ${p.customerName}` : `Promise Due: ${p.customerName}`,
        description: `${p.confirmationMethod} - ${currencySymbol}${p.promisedAmount.toLocaleString()}`,
        priority,
        status: taskStatus,
        dueDate: p.promiseDate,
        assignedTo: p.createdBy || 'Unassigned',
        customerId: p.customerId,
        customerName: p.customerName,
        amount: p.promisedAmount,
        sourceType: 'Promise to Pay',
        sourceId: p.id,
        sourceUrl: `/collections/promises?highlight=${p.id}`,
        createdAt: p.createdAt,
        updatedAt: p.createdAt,
        tags: [p.status, p.confirmationMethod],
      };
    });
}

/**
 * Map disputes to unified tasks
 */
// biome-ignore lint/suspicious/noExplicitAny: pre-existing
function mapDisputesToTasks(disputes: any[]): UnifiedTask[] {
  return disputes
    .filter(
      (d) =>
        d.requiresResponse &&
        (d.status === 'In Progress' || d.status === 'Under Review' || d.status === 'New' || d.status === 'Escalated'),
    )
    .map((d, index) => {
      // All active disputes are open tasks
      const taskStatus: TaskStatus = 'open';

      return {
        id: `dispute-${d.id}`,
        type: 'dispute' as const,
        title: `Dispute Response: ${d.customer}`,
        description: `${d.type} - ${d.description}`,
        priority: d.priority,
        status: taskStatus,
        dueDate: d.dueDate,
        assignedTo: d.assignedTo || 'Unassigned',
        customerId: d.customerId,
        customerName: d.customer,
        amount: d.amount,
        sourceType: 'Dispute',
        sourceId: d.id,
        sourceUrl: `/disputes/all?highlight=${d.id}`,
        createdAt: d.createdDate,
        updatedAt: d.createdDate,
        tags: [d.type, d.status, `Level ${d.currentLevel}`],
      };
    });
}

/**
 * Map unallocated receipts to unified tasks
 */
// biome-ignore lint/suspicious/noExplicitAny: pre-existing
function mapReceiptsToTasks(receipts: any[], currencySymbol: string): UnifiedTask[] {
  return receipts
    .filter((r) => r.allocationStatus === 'unallocated' && r.unappliedAmount > 0)
    .map((r, index) => {
      // All unallocated receipts are open tasks
      const taskStatus: TaskStatus = 'open';

      return {
        id: `receipt-${r.receiptNumber}`,
        type: 'transaction' as const,
        title: `Allocate Receipt: ${r.customerName}`,
        description: `${r.paymentMethod} - ${currencySymbol}${r.unappliedAmount.toLocaleString()} unallocated`,
        priority: r.unappliedAmount > 10000 ? 'High' : 'Medium',
        status: taskStatus,
        dueDate: r.receiptDate, // Use receipt date as reference
        assignedTo: 'Unassigned',
        customerId: r.customerId,
        customerName: r.customerName,
        amount: r.unappliedAmount,
        sourceType: 'Receipt',
        sourceId: r.receiptNumber,
        sourceUrl: `/payments/receipts?highlight=${r.receiptNumber}`,
        createdAt: r.receiptDate,
        updatedAt: r.receiptDate,
        tags: [r.allocationStatus, r.paymentMethod],
      };
    });
}

/**
 * Map standalone tasks to unified tasks
 */
// biome-ignore lint/suspicious/noExplicitAny: pre-existing
function mapStandaloneTasksToTasks(standaloneTasks: any[]): UnifiedTask[] {
  return standaloneTasks.map((t) => {
    return {
      id: t.id,
      type: 'task' as const,
      title: t.title,
      description: t.description,
      priority: t.priority,
      status: t.status,
      dueDate: t.dueDate,
      assignedTo: t.assignedTo,
      customerId: t.customerId || '',
      customerName: t.customerName || 'N/A',
      amount: t.amount,
      sourceType: 'Task',
      sourceId: t.id,
      sourceUrl: `/tasks?highlight=${t.id}`,
      createdAt: t.createdAt,
      updatedAt: t.updatedAt,
      tags: ['Task'],
    };
  });
}

/**
 * Apply filters to tasks
 */
function applyFilters(tasks: UnifiedTask[], filters: TaskFilters): UnifiedTask[] {
  let filtered = [...tasks];

  // Filter by type
  if (filters.type && filters.type.length > 0) {
    filtered = filtered.filter((t) => filters.type!.includes(t.type));
  }

  // Filter by priority
  if (filters.priority && filters.priority.length > 0) {
    filtered = filtered.filter((t) => filters.priority!.includes(t.priority));
  }

  // Filter by status
  if (filters.status && filters.status.length > 0) {
    filtered = filtered.filter((t) => filters.status!.includes(t.status));
  }

  // Filter by assigned to
  if (filters.assignedTo && filters.assignedTo.length > 0) {
    filtered = filtered.filter((t) => filters.assignedTo!.includes(t.assignedTo));
  }

  // Filter by date range
  if (filters.dateRange) {
    filtered = filtered.filter((t) =>
      isWithinInterval(t.dueDate, {
        start: filters.dateRange!.start,
        end: filters.dateRange!.end,
      }),
    );
  }

  // Filter by search query
  if (filters.searchQuery && filters.searchQuery.trim() !== '') {
    const query = filters.searchQuery.toLowerCase();
    filtered = filtered.filter(
      (t) =>
        t.title.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query) ||
        t.customerName.toLowerCase().includes(query),
    );
  }

  return filtered;
}

/**
 * Sort tasks by priority and due date
 */
function sortTasks(tasks: UnifiedTask[]): UnifiedTask[] {
  const priorityOrder = { Critical: 0, High: 1, Medium: 2, Low: 3 };

  return [...tasks].sort((a, b) => {
    // First sort by priority
    const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
    if (priorityDiff !== 0) return priorityDiff;

    // Then by due date (earlier first)
    return a.dueDate.getTime() - b.dueDate.getTime();
  });
}

/**
 * Main hook for task board
 */
export function useTaskBoard(initialFilters?: TaskFilters, currencySymbol: string = '£') {
  const [filters, setFilters] = useState<TaskFilters>(initialFilters || {});
  const [localTaskStatuses, setLocalTaskStatuses] = useState<Record<string, TaskStatus>>({});

  // Aggregate all tasks
  const allTasks = useMemo(() => {
    const store = getGlobalDataStore();

    const allCases = store.getAllCases();
    const allPromises = store.getAllPromises();
    const allDisputes = store.getAllDisputes();
    const allReceipts = store.getAllReceipts();
    const allStandaloneTasks = store.getAllStandaloneTasks();

    const collectionTasks = mapCollectionCasesToTasks(allCases);
    const promiseTasks = mapPromisesToTasks(allPromises, currencySymbol);
    const disputeTasks = mapDisputesToTasks(allDisputes);
    const receiptTasks = mapReceiptsToTasks(allReceipts, currencySymbol);
    const standaloneTasks = mapStandaloneTasksToTasks(allStandaloneTasks);

    // Merge all tasks and apply local status overrides
    const merged = [...collectionTasks, ...promiseTasks, ...disputeTasks, ...receiptTasks, ...standaloneTasks].map(
      (task) => ({
        ...task,
        status: localTaskStatuses[task.id] || task.status,
      }),
    );

    return sortTasks(merged);
  }, [localTaskStatuses, currencySymbol]);

  // Apply filters
  const filteredTasks = useMemo(() => {
    return applyFilters(allTasks, filters);
  }, [allTasks, filters]);

  // Group tasks by status for columns
  const columns: TaskBoardColumn[] = useMemo(() => {
    const statusGroups: Record<TaskStatus, UnifiedTask[]> = {
      open: [],
      completed: [],
    };

    filteredTasks.forEach((task) => {
      statusGroups[task.status].push(task);
    });

    return [
      { id: 'open', title: 'Open', tasks: statusGroups.open },
      { id: 'completed', title: 'Completed', tasks: statusGroups.completed },
    ];
  }, [filteredTasks]);

  // Task management functions
  const updateTaskStatus = (taskId: string, newStatus: TaskStatus) => {
    setLocalTaskStatuses((prev) => ({
      ...prev,
      [taskId]: newStatus,
    }));
  };

  const completeTask = (taskId: string) => {
    updateTaskStatus(taskId, 'completed');
  };

  const updateFilters = (newFilters: Partial<TaskFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const replaceFilters = (newFilters: TaskFilters) => {
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
  };

  // Statistics
  const stats = useMemo(() => {
    const overdue = filteredTasks.filter(
      (t) => t.status !== 'completed' && isPast(t.dueDate) && !isToday(t.dueDate),
    ).length;

    const dueToday = filteredTasks.filter((t) => t.status !== 'completed' && isToday(t.dueDate)).length;

    const upcoming = filteredTasks.filter((t) => t.status !== 'completed' && isFuture(t.dueDate)).length;

    const completed = filteredTasks.filter((t) => t.status === 'completed').length;

    return { overdue, dueToday, upcoming, completed, total: filteredTasks.length };
  }, [filteredTasks]);

  const refreshTasks = () => {
    // Force re-render by clearing local statuses
    setLocalTaskStatuses({});
  };

  return {
    tasks: filteredTasks,
    allTasks: filteredTasks, // Expose filtered tasks for list and calendar views
    columns,
    filters,
    setFilters: updateFilters,
    updateFilters,
    replaceFilters,
    clearFilters,
    handleTaskStatusChange: updateTaskStatus,
    updateTaskStatus,
    completeTask,
    refreshTasks,
    stats,
  };
}
